import React, { useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

const GEO_URL = "/world-110m.json";
const ALIAS = { "United States of America": "United States" };

export const WorldMap = ({ countryCounts = [], active, onSelect }) => {
  const [tooltip, setTooltip] = useState(null); // {name, count, x, y}
  const countMap = React.useMemo(() => {
    const m = {};
    countryCounts.forEach((c) => (m[c.country] = c.count));
    return m;
  }, [countryCounts]);

  const nameFor = (geoName) => ALIAS[geoName] || geoName;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0d1020]"
      data-testid="world-map"
    >
      <div className="pointer-events-none absolute left-4 top-4 z-10 hidden items-center gap-3 rounded-lg bg-[#0d1020]/85 px-2.5 py-1.5 text-xs text-slate-300 backdrop-blur-sm sm:flex">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-blue-500" /> Has shows
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-amber-400" /> Selected
        </span>
      </div>

      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{ scale: 165 }}
        style={{ width: "100%", height: "auto" }}
        height={380}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const name = nameFor(geo.properties.name);
              const count = countMap[name] || 0;
              const isActive = active && active !== "all" && active === name;
              const hasShows = count > 0;
              const fill = isActive ? "#F59E0B" : hasShows ? "#3B82F6" : "rgba(255,255,255,0.05)";
              const hoverFill = isActive ? "#FBBF24" : hasShows ? "#60A5FA" : "rgba(255,255,255,0.12)";
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  data-testid={hasShows ? `map-country-${name.replace(/\s+/g, "-").toLowerCase()}` : undefined}
                  onMouseEnter={(e) =>
                    setTooltip({ name, count, x: e.clientX, y: e.clientY })
                  }
                  onMouseMove={(e) =>
                    setTooltip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : t))
                  }
                  onMouseLeave={() => setTooltip(null)}
                  onClick={() => hasShows && onSelect(name)}
                  style={{
                    default: { fill, stroke: "#0B0D17", strokeWidth: 0.5, outline: "none" },
                    hover: {
                      fill: hoverFill,
                      stroke: "#0B0D17",
                      strokeWidth: 0.5,
                      outline: "none",
                      cursor: hasShows ? "pointer" : "default",
                    },
                    pressed: { fill: "#F59E0B", outline: "none" },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {tooltip && (
        <div
          className="pointer-events-none fixed z-50 rounded-lg border border-white/15 bg-[#12141F] px-3 py-1.5 text-xs shadow-xl"
          style={{ left: Math.min(tooltip.x + 12, window.innerWidth - 170), top: tooltip.y + 12 }}
        >
          <span className="font-semibold text-white">{tooltip.name}</span>
          <span className="ml-2 text-slate-400">
            {tooltip.count > 0 ? `${tooltip.count} show${tooltip.count === 1 ? "" : "s"}` : "No shows yet"}
          </span>
        </div>
      )}
    </div>
  );
};
